import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";

export interface SlackChannel {
  id: string;
  name: string;
  is_channel?: boolean;
  is_group?: boolean;
  is_im?: boolean;
  is_mpim?: boolean;
  is_private?: boolean;
  is_archived?: boolean;
  /** True when the resolved target is a user (ID starts with U/W), not a channel. */
  is_user?: boolean;
  /**
   * True when the bot is a member of this conversation. Returned by
   * conversations.info; used to warn when an author selects a private
   * channel the bot cannot post to.
   */
  is_member?: boolean;
  num_members?: number;
}

/** Slack ID namespace: C (public), G (private/legacy), D (DM), U (user), W (enterprise user). */
export const SLACK_ID_PATTERN = /^[CGDUW][A-Z0-9]{8,}$/i;

export function isSlackId(value: string): boolean {
  return SLACK_ID_PATTERN.test(value);
}

function isSlackChannel(value: unknown): value is SlackChannel {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as SlackChannel).id === "string" &&
    typeof (value as SlackChannel).name === "string"
  );
}

const MAX_PAGES = 5;

export interface FetchSlackChannelsOptions {
  /**
   * Include private channels the bot can see. Defaults to false because the
   * selector dropdown should not leak private channel names to b3os org
   * members who aren't in those Slack channels. Pass true for read-only
   * label-resolution callers (run history, node summary, JSON views) where
   * the user is looking at their own saved selection, not discovering new
   * channels.
   */
  includePrivate?: boolean;
}

export async function fetchSlackChannels(
  connectorId: string,
  connectorType: string,
  options: FetchSlackChannelsOptions = {},
): Promise<SlackChannel[]> {
  const includePrivate = options.includePrivate ?? false;
  const allChannels: SlackChannel[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    const inputs: Record<string, unknown> = { limit: 1000 };
    if (cursor) inputs.cursor = cursor;

    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/slack-list-channels/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        inputs,
        connector: { id: connectorId, type: connectorType },
      }),
    });

    if (!response.ok) {
      if (allChannels.length === 0) throw new Error(`Failed to fetch Slack channels (HTTP ${response.status})`);
      console.warn(`[SlackChannels] Pagination stopped at page ${page + 1}: HTTP ${response.status}`);
      break;
    }

    const data = await response.json();
    const ret = data?.data?.result?.data?.ret;
    if (!ret || typeof ret !== "object") {
      console.warn("[SlackChannels] Unexpected response shape", { page: page + 1, keys: Object.keys(data ?? {}) });
      break;
    }

    const { channels, next_cursor: nextCursor } = ret;

    if (Array.isArray(channels)) {
      allChannels.push(...channels.filter(isSlackChannel));
    }

    if (!nextCursor) break;
    cursor = nextCursor;
  }

  // Deduplicate and exclude archived — use a loop so an archived entry
  // doesn't shadow a later active duplicate with the same ID. Private
  // channels are included when the caller opts in (see options docs).
  const seen = new Set<string>();
  const uniqueChannels: SlackChannel[] = [];
  for (const ch of allChannels) {
    if (seen.has(ch.id) || ch.is_archived) continue;
    if (!includePrivate && ch.is_private) continue;
    seen.add(ch.id);
    uniqueChannels.push(ch);
  }
  return uniqueChannels;
}

/**
 * Adapt a SlackUser (from slackUsers.ts) to the SlackChannel shape so the
 * selector can render users and channels from a single dropdown index.
 * Keeps both the display name (for @-search) and the Slack username as a
 * fallback-friendly "name" — display_name wins, then real_name, then name.
 * Returns null for deleted/bot users so the caller can filter them out.
 */
export function slackUserToChannel(user: {
  id: string;
  name: string;
  real_name?: string;
  is_bot?: boolean;
  deleted?: boolean;
  profile?: { display_name?: string; real_name?: string };
}): SlackChannel | null {
  if (user.deleted || user.is_bot || user.id === "USLACKBOT") return null;
  const displayName = user.profile?.display_name || user.real_name || user.profile?.real_name || user.name || user.id;
  return {
    id: user.id,
    name: displayName,
    is_user: true,
  };
}

/**
 * Result shape for fetchSlackChannelById — distinguishes success, benign
 * miss (channel really not found / bot not a member), and transport errors
 * so the UI can show an appropriate hint instead of collapsing everything
 * into "bot not in channel".
 */
export type SlackChannelLookup =
  | { status: "found"; channel: SlackChannel }
  | { status: "not_found" }
  | { status: "error"; message: string };

/**
 * Resolve a single Slack channel (or user) by ID via slack-get-channel-info.
 * Used when the user pastes a channel ID directly — we look it up so
 * private channels (which we intentionally exclude from the main list)
 * can still be selected when the caller knows the ID.
 */
export async function fetchSlackChannelById(
  connectorId: string,
  connectorType: string,
  channelId: string,
): Promise<SlackChannelLookup> {
  let response: Response;
  try {
    response = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/slack-get-channel-info/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        inputs: { channel: channelId },
        connector: { id: connectorId, type: connectorType },
      }),
    });
  } catch (err) {
    return { status: "error", message: err instanceof Error ? err.message : "Network error" };
  }
  if (!response.ok) {
    // HTTP 404 on the action-proxy endpoint means b3os-workflow hasn't
    // registered slack-get-channel-info yet (e.g. frontend rolled out
    // ahead of the workflow/actions deploy). Treat as benign not_found
    // so existing nodes with private-channel IDs degrade gracefully
    // during the deploy window instead of permanently marking the IDs
    // as "lookup failed".
    if (response.status === 404) return { status: "not_found" };
    return { status: "error", message: `HTTP ${response.status}` };
  }
  let data: unknown;
  try {
    data = await response.json();
  } catch {
    return { status: "error", message: "Malformed response" };
  }
  const result = (
    data as {
      data?: {
        result?: {
          success?: boolean;
          error?: { code?: string; message?: string; details?: { slackError?: string } };
        };
      };
    }
  )?.data?.result;
  if (result?.success === false) {
    const slackErr = result.error?.details?.slackError;
    // Benign misses the UI collapses into "not_found or bot not a member":
    // everything else (invalid_auth, token_revoked, missing_scope,
    // ratelimited, account_inactive, HTTP 5xx wrapped as SLACK_API_ERROR,
    // etc.) surfaces as a transport-level error so the user isn't
    // misdirected into inviting the bot.
    const benignMisses = new Set(["channel_not_found", "user_not_found", "not_in_channel", "is_archived"]);
    if (slackErr && benignMisses.has(slackErr)) return { status: "not_found" };
    const msg = slackErr ?? result.error?.message ?? result.error?.code ?? "Lookup failed";
    return { status: "error", message: msg };
  }
  const channel = (result as { data?: { ret?: { channel?: unknown } } })?.data?.ret?.channel;
  if (!isSlackChannel(channel)) return { status: "not_found" };
  if (channel.is_archived) return { status: "not_found" };
  return { status: "found", channel };
}

export function resolveSlackConversationLabel(value: string, channelsById?: Map<string, SlackChannel>): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("#") || trimmed.startsWith("@")) return trimmed;

  const channel = channelsById?.get(trimmed);
  if (channel?.name) {
    return channel.is_user ? `@${channel.name}` : `#${channel.name}`;
  }

  // Hide raw Slack IDs in the node summary when we can't resolve them.
  // Distinguish user targets (U/W IDs → DMs) from channel targets so the
  // summary doesn't mislabel a user DM as "Slack channel".
  if (/^[UW][A-Z0-9]{8,}$/i.test(trimmed)) {
    return "Slack user";
  }
  if (isSlackId(trimmed)) {
    return "Slack channel";
  }

  return trimmed;
}
